import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Leaderboard — v2 port of v1's leaderboard.ejs.
 * Similar to Supporters but more visual + multiple metric modes:
 *   - tips: rank by tips/cheers/super-chat
 *   - follows: rank by which streamers people follow (single-stream — rank by who follows oftenest is moot, so this is N/A)
 *   - hype: rank by total hype-meter contribution (event weights)
 *
 * Config (all optional):
 *   maxRows: number              (default 5)
 *   accentColor: string          (default '#a78bfa')
 *   title: string                (default 'Leaderboard')
 *   metric: 'tips' | 'hype'      (default 'tips')
 *   compact: boolean             (default false)
 *   highlightFirstPlace: boolean (default true)
 */

const HYPE_WEIGHTS = {
  follow: 5,
  sub: 25,
  subGift: 30,
  cheerPer100: 10,
  raid: 40,
  tipPer100: 1,
  superChatPerDollar: 20,
  cardRedeem: 10,
};

function tipValue(e: StreamEvent): { name: string; value: number } | null {
  switch (e.type) {
    case 'tip':
      return { name: e.viewerName, value: e.coins };
    case 'tip_received':
      return { name: e.viewerName, value: e.amountCents };
    case 'cheer':
      return { name: e.viewerName, value: e.bits };
    case 'superChat':
    case 'superSticker':
      return { name: e.viewerName, value: e.amountCents };
    default:
      return null;
  }
}

function hypeValue(e: StreamEvent): { name: string; value: number } | null {
  switch (e.type) {
    case 'follow':
      return { name: e.viewerName, value: HYPE_WEIGHTS.follow };
    case 'sub':
      return {
        name: e.isGift ? (e.gifterName ?? e.viewerName) : e.viewerName,
        value: e.isGift ? HYPE_WEIGHTS.subGift : HYPE_WEIGHTS.sub,
      };
    case 'cheer':
      return { name: e.viewerName, value: (e.bits / 100) * HYPE_WEIGHTS.cheerPer100 };
    case 'raid':
      return { name: e.raiderName, value: HYPE_WEIGHTS.raid };
    case 'tip':
      return { name: e.viewerName, value: (e.coins / 100) * HYPE_WEIGHTS.tipPer100 };
    case 'tip_received':
      return { name: e.viewerName, value: (e.amountCents / 100) * HYPE_WEIGHTS.tipPer100 };
    case 'superChat':
    case 'superSticker':
      return {
        name: e.viewerName,
        value: (e.amountCents / 100) * HYPE_WEIGHTS.superChatPerDollar,
      };
    case 'cardRedemption':
      return { name: e.viewerName, value: HYPE_WEIGHTS.cardRedeem };
    default:
      return null;
  }
}

const MEDALS = ['🥇', '🥈', '🥉'];

export function LeaderboardWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const maxRows = Number(fields.maxRows ?? 5);
  const accent = String(fields.accentColor ?? '#a78bfa');
  const title = String(fields.title ?? 'Leaderboard');
  const metric = (fields.metric as 'tips' | 'hype' | undefined) ?? 'tips';
  const compact = (fields.compact as boolean | undefined) ?? false;
  const highlightFirstPlace = (fields.highlightFirstPlace as boolean | undefined) ?? true;

  const [totals, setTotals] = useState<Map<string, number>>(new Map());
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    const c = metric === 'tips' ? tipValue(latestEvent) : hypeValue(latestEvent);
    if (!c) return;
    setTotals((prev) => {
      const next = new Map(prev);
      next.set(c.name, (next.get(c.name) ?? 0) + c.value);
      return next;
    });
  }, [latestEvent, metric]);

  const rows = Array.from(totals.entries())
    .map(([name, total]) => ({ name, total }))
    .sort((a, b) => b.total - a.total)
    .slice(0, maxRows);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 14,
        padding: compact ? '10px 14px' : '14px 20px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 280,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          right: 22,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1.6,
          textTransform: 'uppercase',
          color: accent,
          marginBottom: 10,
          textAlign: 'center',
        }}
      >
        {title}
      </div>
      {rows.length === 0 ? (
        <div
          style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.3)',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          Waiting for activity…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 5 : 7 }}>
          {rows.map((r, i) => {
            const isFirst = i === 0 && highlightFirstPlace;
            const medal = MEDALS[i] ?? null;
            return (
              <div
                key={r.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: compact ? '3px 8px' : '6px 10px',
                  borderRadius: 8,
                  background: isFirst ? `${accent}1a` : 'rgba(255,255,255,0.02)',
                  border: isFirst ? `1px solid ${accent}40` : '1px solid transparent',
                }}
              >
                <span
                  style={{
                    fontSize: medal ? 18 : 12,
                    fontWeight: 800,
                    width: 22,
                    textAlign: 'center',
                    color: i === 0 ? accent : 'rgba(255,255,255,0.5)',
                  }}
                >
                  {medal ?? `${i + 1}`}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: compact ? 13 : 15,
                    fontWeight: 700,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {r.name}
                </span>
                <span
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: compact ? 12 : 14,
                    fontWeight: 800,
                    color: accent,
                  }}
                >
                  {metric === 'tips'
                    ? r.total.toLocaleString()
                    : Math.round(r.total).toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
